import { Truck, UsersRound } from 'lucide-react'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TabsContent } from '@/components/ui/tabs'
import { ToggleTabs } from '@/components/ui/ToggleTabs'
import { customerTabHref } from '@/lib/customerTabs'
import { approveDriver, rejectDriver } from '../../../../models/driver'
import { approveTrailer, approveTruck, rejectTrailer, rejectTruck } from '../../../../models/equipment'
import {
  PendingEquipmentChangeKind,
  type PendingEquipmentChangeRow,
  usePendingEquipmentChanges,
} from '../../../../models/pendingEquipmentChange'

const KIND_LABELS: Record<PendingEquipmentChangeKind, string> = {
  [PendingEquipmentChangeKind.TRUCK]: 'Truck',
  [PendingEquipmentChangeKind.TRAILER]: 'Trailer',
  [PendingEquipmentChangeKind.DRIVER]: 'Driver',
}

// The customer detail page names its tab in the path (see CustomerDetail).
const CUSTOMER_TAB: Record<PendingEquipmentChangeKind, string> = {
  [PendingEquipmentChangeKind.TRUCK]: 'trucks',
  [PendingEquipmentChangeKind.TRAILER]: 'trailers',
  [PendingEquipmentChangeKind.DRIVER]: 'drivers',
}

function approveFn(kind: PendingEquipmentChangeKind) {
  if (kind === PendingEquipmentChangeKind.TRUCK) return approveTruck
  if (kind === PendingEquipmentChangeKind.TRAILER) return approveTrailer
  return approveDriver
}

function rejectFn(kind: PendingEquipmentChangeKind) {
  if (kind === PendingEquipmentChangeKind.TRUCK) return rejectTruck
  if (kind === PendingEquipmentChangeKind.TRAILER) return rejectTrailer
  return rejectDriver
}

function PendingRowActions({
  row,
  onDone,
}: {
  row: PendingEquipmentChangeRow
  onDone: () => Promise<unknown>
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handle(action: 'approve' | 'reject') {
    setIsSubmitting(true)
    try {
      const fn = action === 'approve' ? approveFn(row.kind) : rejectFn(row.kind)
      await fn(row.orgId, row.id)
      await onDone()
    } catch {
      toast.error('Failed to update pending change')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Button size="sm" disabled={isSubmitting} onClick={() => handle('approve')}>
        Approve
      </Button>
      <Button variant="outline" size="sm" disabled={isSubmitting} onClick={() => handle('reject')}>
        Reject
      </Button>
    </div>
  )
}

function PendingChangesTable({
  rows,
  showKind,
  emptyLabel,
  onDone,
}: {
  rows: PendingEquipmentChangeRow[]
  showKind: boolean
  emptyLabel: string
  onDone: () => Promise<unknown>
}) {
  const navigate = useNavigate()
  const colSpan = showKind ? 5 : 4

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-semibold">Org</TableHead>
          {showKind && <TableHead className="font-semibold">Kind</TableHead>}
          <TableHead className="font-semibold">Status</TableHead>
          <TableHead className="font-semibold">Label</TableHead>
          <TableHead className="font-semibold"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={colSpan} className="pt-5 text-center">
              {emptyLabel}
            </TableCell>
          </TableRow>
        )}
        {rows.map((row) => (
          <TableRow
            key={`${row.kind}-${row.id}`}
            className="cursor-pointer"
            onClick={() => navigate(customerTabHref(row.orgId, CUSTOMER_TAB[row.kind]))}
          >
            <TableCell className="font-medium">{row.orgName}</TableCell>
            {showKind && (
              <TableCell>
                <Badge variant="outline">{KIND_LABELS[row.kind]}</Badge>
              </TableCell>
            )}
            <TableCell>
              <Badge variant="secondary">{row.status}</Badge>
            </TableCell>
            <TableCell>{row.label}</TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              <PendingRowActions row={row} onDone={onDone} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export function PendingEquipmentChanges() {
  const { rows, isLoading, refetch } = usePendingEquipmentChanges()

  const equipmentRows = rows.filter(
    (row) => row.kind === PendingEquipmentChangeKind.TRUCK || row.kind === PendingEquipmentChangeKind.TRAILER
  )
  const driverRows = rows.filter((row) => row.kind === PendingEquipmentChangeKind.DRIVER)

  return (
    <>
      <Helmet>
        <title>Pending Changes</title>
      </Helmet>
      <div className="flex flex-col gap-5">
        <div className="mb-2 text-2xl font-semibold">Pending Changes</div>
        {isLoading ? (
          <SkeletonRows rows={6} />
        ) : (
          <ToggleTabs
            defaultValue="equipment"
            className="w-full"
            items={[
              { value: 'equipment', icon: <Truck />, label: `Equipment (${equipmentRows.length})` },
              { value: 'drivers', icon: <UsersRound />, label: `Drivers (${driverRows.length})` },
            ]}
          >
            <TabsContent value="equipment">
              <PendingChangesTable
                rows={equipmentRows}
                showKind
                emptyLabel="No pending equipment changes"
                onDone={refetch}
              />
            </TabsContent>
            <TabsContent value="drivers">
              <PendingChangesTable
                rows={driverRows}
                showKind={false}
                emptyLabel="No pending driver changes"
                onDone={refetch}
              />
            </TabsContent>
          </ToggleTabs>
        )}
      </div>
    </>
  )
}
